// EligibilityRequirements.js - Australia
import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, FileText, Briefcase, DollarSign, Languages, GraduationCap } from 'lucide-react';

const EligibilityRequirements = () => {
  const requirements = [
    {
      icon: Briefcase,
      title: 'Skilled Occupation',
      description: 'Occupation must be on relevant skilled occupation list (MLTSSL, STSOL, or ROL). Requires positive skills assessment from designated assessing authority for your occupation.',
      applicable: 'GSM, State Nomination'
    },
    {
      icon: Languages,
      title: 'English Proficiency',
      description: 'Minimum Competent English (IELTS 6.0 each band or equivalent). Higher scores earn more points: Proficient (IELTS 7.0) = 10 points, Superior (IELTS 8.0) = 20 points.',
      applicable: 'All Programs'
    },
    {
      icon: GraduationCap,
      title: 'Qualifications',
      description: 'Relevant qualifications for nominated occupation. Australian qualifications provide additional points. Doctorate = 20 points, Bachelor = 15 points.',
      applicable: 'GSM, State Nomination'
    },
    {
      icon: CheckCircle2,
      title: 'Points Score',
      description: 'Minimum 65 points required to submit EOI. Competitive scores typically 80-90+ for invitation. Points based on age, English, experience, education, and other factors.',
      applicable: 'GSM (189, 190, 491)'
    },
    {
      icon: DollarSign,
      title: 'Age Requirement',
      description: 'Must be under 45 years at time of invitation. Maximum points (30) for ages 25-32. Age 33-39 = 25 points, 40-44 = 15 points.',
      applicable: 'GSM, State Nomination'
    },
    {
      icon: FileText,
      title: 'Health & Character',
      description: 'Must meet health requirements (medical examination) and character requirements (police certificates from all countries lived in 12+ months). No substantial criminal record.',
      applicable: 'All Programs'
    }
  ];

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-semibold text-[#202356] mb-4">
            General Eligibility Requirements
          </h2>
          <p className="text-gray-600 text-base font-light max-w-3xl mx-auto">
            While specific requirements vary by program, here are the common criteria for Australian permanent residency applications.
          </p>
        </motion.div>

        {/* Requirements Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {requirements.map((requirement, index) => {
            const Icon = requirement.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-[#F5F8FA] p-6 rounded-lg border border-gray-200 hover:border-[#2542D4] transition-colors"
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon size={20} className="text-[#202356]" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-[#202356] mb-1">
                      {requirement.title}
                    </h3>
                    <span className="text-xs text-[#2542D4] font-light">
                      {requirement.applicable}
                    </span>
                  </div>
                </div>
                <p className="text-gray-600 text-sm font-light leading-relaxed">
                  {requirement.description}
                </p>
              </motion.div>
            );
          })}
        </div>

        {/* Points Breakdown */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mt-12 bg-gradient-to-r from-[#202356] to-[#2542D4] rounded-lg p-8 text-white"
        >
          <h3 className="text-xl font-semibold mb-4">
            Points Test Breakdown
          </h3>
          <p className="text-white/90 text-sm font-light leading-relaxed mb-4">
            The Australian points test assesses various factors. Minimum 65 points required, but competitive scores are typically 80-90+.
          </p>
          <div className="grid md:grid-cols-4 gap-4 text-sm">
            <div className="bg-white/10 rounded-lg p-4">
              <p className="font-semibold mb-1">Age (25-32)</p>
              <p className="text-white/80 font-light">30 points</p>
            </div>
            <div className="bg-white/10 rounded-lg p-4">
              <p className="font-semibold mb-1">English (Superior)</p>
              <p className="text-white/80 font-light">20 points</p>
            </div>
            <div className="bg-white/10 rounded-lg p-4">
              <p className="font-semibold mb-1">Work Experience (8+ yrs)</p>
              <p className="text-white/80 font-light">20 points</p>
            </div>
            <div className="bg-white/10 rounded-lg p-4">
              <p className="font-semibold mb-1">Education (Doctorate)</p>
              <p className="text-white/80 font-light">20 points</p>
            </div>
          </div>
        </motion.div>

        {/* Important Note */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 1 }}
          className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-6"
        >
          <p className="text-gray-700 text-sm font-light leading-relaxed">
            <strong>Note:</strong> Points requirements and invitation rounds change based on immigration targets and demand. We assess your profile against current requirements and recommend the best pathway for your situation.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default EligibilityRequirements;
